<script setup lang="ts">
/* One row in the sidebar. Owns its own collapsed styling via a
   data attribute on the link, so the parent never has to reach
   across a component boundary with :deep(). */
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import Icon from '@/components/Icon.vue'
import Badge from '@/components/ui/Badge.vue'
import type { NavItem } from '@/config/nav.config'

const props = defineProps<{ item: NavItem; collapsed: boolean }>()

const route = useRoute()

// Same prefix semantics as findNavItemByPath(): /locations/42 keeps
// "Docking locations" lit, unless the item opts out of child matching.
const isActive = computed(() => {
  const { to, matchChildren } = props.item
  if (route.path === to) return true
  return matchChildren === false ? false : route.path.startsWith(`${to}/`)
})
</script>

<template>
  <li>
    <RouterLink
      :to="item.to"
      class="link"
      :class="{ linkActive: isActive }"
      :data-collapsed="collapsed || undefined"
      :title="collapsed ? item.label : undefined"
    >
      <span class="linkIcon">
        <Icon :name="item.icon" :size="20" />
      </span>
      <span class="linkLabel">{{ item.label }}</span>
      <Badge v-if="item.badge" tone="neutral" class="linkBadge">{{ item.badge }}</Badge>
    </RouterLink>
  </li>
</template>

<style scoped>
.link {
  display: flex;
  align-items: center;
  gap: var(--space-2xs);
  height: 2.5rem;
  padding-inline: var(--space-2xs);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--color-text-muted);
  text-decoration: none;
  border-radius: var(--radius-sm);
  transition:
    color var(--dur-fast) var(--ease),
    background-color var(--dur-fast) var(--ease);
}

.link:hover {
  color: var(--color-heading);
  background-color: var(--color-surface-sunken);
}

.linkActive,
.linkActive:hover {
  color: var(--color-accent-hover);
  background-color: var(--color-accent-soft);
  font-weight: var(--weight-semibold);
}

.linkIcon {
  display: grid;
  place-items: center;
  flex: none;
  width: 1.5rem;
}

.linkLabel {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.linkBadge {
  flex: none;
}

/* --- collapsed: icons only ------------------------------------ */

.link[data-collapsed] {
  justify-content: center;
  padding-inline: 0;
}

.link[data-collapsed] .linkLabel,
.link[data-collapsed] .linkBadge {
  display: none;
}
</style>
